import type { Database } from "@cohesive/api-entities";
import { formatDistanceToNow } from "date-fns";
import { Rows3 } from "lucide-react";
import { Skeleton } from "../../ui/skeleton";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardProps,
  BaseEntityCardTitle,
} from "../BaseCard";

export type DatabaseCardProps = Database & BaseEntityCardProps;

export function DatabaseCardAvatar({
  className,
  style,
}: Partial<DatabaseCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Rows3 className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function DatabaseCard({
  platform,
  title,
  updated_at,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: DatabaseCardProps) {
  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <DatabaseCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>{title}</BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open database"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardMetadata>
            {updated_at && (
              <span>
                Updated{" "}
                {formatDistanceToNow(new Date(updated_at), {
                  addSuffix: true,
                })}
              </span>
            )}
          </BaseEntityCardMetadata>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function DatabaseCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={className} style={style}>
      <Skeleton className="h-10 w-10 rounded-lg" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-2.5" />
        <Skeleton className="h-3 w-1/4 mb-1.5" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
